import { Button, ButtonGroup, HTMLSelect } from "@blueprintjs/core"
import { useEffect, useState } from "react"
import { FundamentalMetricSuggest } from "../../../../components/FundamentalMetricSuggest"
import { StyledContainer, StyledPeriodicityContainer } from "./FundamentalPanel.styled"
import TickerSuggest from "../../Config/components/TickerSuggest"
import useTradingCompanies from "../../Config/hooks/useTradingCompanies"
import { TradingCompany } from "../../Config/types"
import {
  FundamentalsPeriodicity,
  FundamentalsSource,
  FundamentalsTransformationType,
} from "../types/dashboard"
import { DashboardItemForm } from "../types/dashboardForm"

interface FundamentalsPanelProps {
  onChangeCandidate: (item: DashboardItemForm | null) => void
}

export const FundamentalsPanel = ({ onChangeCandidate }: FundamentalsPanelProps) => {
  const [selectedCompany, setSelectedCompany] = useState<TradingCompany | undefined>(undefined)
  const [periodicity, setPeriodicity] = useState<FundamentalsPeriodicity>(
    FundamentalsPeriodicity.QUARTERLY,
  )
  const [metric, setMetric] = useState<string>("")
  const [transformation, setTransformation] = useState<FundamentalsTransformationType>(
    FundamentalsTransformationType.VALUE,
  )

  const useCompaniesWrapper = ({ query }: { query: string }) => {
    const { companies, isLoading, error } = useTradingCompanies({ query })
    return { items: companies ?? [], isLoading, error }
  }

  const canAdd = Boolean(selectedCompany && periodicity && metric.trim().length > 0 && transformation)

  // Reset metric and transformation when periodicity changes
  useEffect(() => {
    setMetric("")
    setTransformation(FundamentalsTransformationType.VALUE)
  }, [periodicity])

  useEffect(() => {
    if (canAdd && selectedCompany) {
      const item: DashboardItemForm = {
        company_id: selectedCompany.id,
        company_main_instrument_ticker: selectedCompany.main_instrument_ticker,
        fundamentals_source: FundamentalsSource.S14,
        fundamentals_periodicity: periodicity,
        fundamentals_metric: metric,
        fundamentals_transformation: transformation,
      }
      onChangeCandidate(item)
    } else {
      onChangeCandidate(null)
    }
  }, [canAdd, selectedCompany, periodicity, metric, transformation, onChangeCandidate])

  return (
    <StyledContainer>
      <StyledPeriodicityContainer>
        <ButtonGroup>
          {[
            { period: FundamentalsPeriodicity.MONTHLY, label: "M" },
            { period: FundamentalsPeriodicity.QUARTERLY, label: "Q" },
            { period: FundamentalsPeriodicity.HALF_YEAR, label: "H" },
            { period: FundamentalsPeriodicity.FISCAL_YEAR, label: "FY" },
          ].map(({ period, label }) => (
            <Button
              key={period}
              active={periodicity === period}
              onClick={() => setPeriodicity(period)}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
        <TickerSuggest<TradingCompany>
          setSelectedItem={setSelectedCompany}
          selectedItem={selectedCompany}
          useItems={useCompaniesWrapper}
          errorMessage="Unable to search companies, please try to refresh the page."
        />
      </StyledPeriodicityContainer>

      <FundamentalMetricSuggest
        company_id={selectedCompany?.id}
        periodicity={periodicity}
        value={metric}
        onSelect={m => setMetric(m ?? "")}
      />

      <HTMLSelect
        value={transformation}
        onChange={e => setTransformation(e.target.value as FundamentalsTransformationType)}
        fill
        large
      >
        <option value={FundamentalsTransformationType.VALUE}>Value</option>
        <option value={FundamentalsTransformationType.PCT_YOY}>%YoY</option>
        <option value={FundamentalsTransformationType.NET_YEARLY_CHANGE}>Net Yearly Change</option>
        <option value={FundamentalsTransformationType.PCT_SOS}>%SoS</option>
        <option value={FundamentalsTransformationType.NET_SEQUENTIAL_CHANGE}>
          Net Sequential Change
        </option>
      </HTMLSelect>
    </StyledContainer>
  )
}

export default FundamentalsPanel
